import React, { useEffect, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import apiService from '../../services/api';
import { formatARS } from '../../utils/formatCurrency';
import { ColorKey } from '../../utils/pricingConfig';
import { COLOR_LABELS, PACK_SIZE, receiptColorStyle } from '../../utils/warehouseStock';
import { SupplierPrice, SupplierPriceData, suggestSupplierPrice } from '../../utils/supplierPrices';

interface PriceForm { id?: number; ancho: number; largo: number; color: ColorKey; microns: number; unitPrice: number; note: string; }

const emptyForm = (): PriceForm => ({ ancho: 45, largo: 60, color: 'negro', microns: 0, unitPrice: 0, note: '' });
const formatDate = (iso: string) => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : '');

const SupplierPriceList: React.FC<{ supplierId: number; isReadOnly: boolean }> = ({ supplierId, isReadOnly }) => {
  const [data, setData] = useState<SupplierPriceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState<PriceForm | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setLoading(true); setError(''); setData(await apiService.getSupplierPrices(supplierId)); }
    catch (e: any) { setError(e?.response?.data?.detail || 'No se pudo cargar la lista de precios.'); }
    finally { setLoading(false); }
  };
  // Se recarga solo al cambiar de proveedor.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [supplierId]);

  const save = async () => {
    if (!form) return;
    try {
      setSaving(true); setError('');
      const { id, ...payload } = form;
      await apiService.saveSupplierPrice(supplierId, payload, id);
      setForm(null);
      await load();
    } catch (e: any) { setError(e?.response?.data?.detail || 'No se pudo guardar el precio.'); }
    finally { setSaving(false); }
  };
  const remove = async (price: SupplierPrice) => {
    if (!window.confirm(`¿Quitar ${price.ancho} × ${price.largo} ${COLOR_LABELS[price.color as ColorKey] || price.color} de la lista?`)) return;
    try { await apiService.deleteSupplierPrice(supplierId, price.id); await load(); }
    catch (e: any) { setError(e?.response?.data?.detail || 'No se pudo quitar el precio.'); }
  };
  const importFromReceipts = async () => {
    try {
      setSaving(true); setError('');
      const { added } = await apiService.importSupplierPricesFromReceipts(supplierId);
      await load();
      window.alert(added ? `Se agregaron ${added} artículo${added === 1 ? '' : 's'} con el último precio pagado. Revisalos y actualizalos si el proveedor aumentó.` : 'No hay artículos nuevos para agregar: todo lo comprado ya está en la lista.');
    } catch (e: any) { setError(e?.response?.data?.detail || 'No se pudo armar la lista desde los remitos.'); }
    finally { setSaving(false); }
  };

  const prices = data?.prices || [];
  const unlisted = (data?.lastPaid || []).filter((item) => suggestSupplierPrice({ prices, lastPaid: [] }, item) === null);

  return (
    <section className="rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="flex flex-wrap items-end justify-between gap-2 border-b px-4 py-4 sm:px-5">
        <div>
          <h2 className="font-semibold text-gray-900">Lista de precios del proveedor</h2>
          <p className="text-xs text-gray-500">Precio por bolsa sin IVA, como figura en sus remitos. Al cargar un remito de este proveedor, el precio de cada línea se completa desde acá.</p>
        </div>
        {!isReadOnly && (
          <div className="flex flex-wrap gap-2">
            <button onClick={importFromReceipts} disabled={saving} className="rounded-lg border px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">Completar con los últimos remitos</button>
            <button onClick={() => setForm(emptyForm())} className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-3 py-2 text-sm text-white hover:bg-primary-700"><Plus className="h-4 w-4" />Agregar artículo</button>
          </div>
        )}
      </div>
      {error && <div className="m-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {form && (
        <div className="m-4 space-y-3 rounded-lg border bg-gray-50 p-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
            <label className="text-xs">Ancho (cm)<input type="number" min={0} value={form.ancho} onChange={(e) => setForm({ ...form, ancho: Number(e.target.value) })} className="mt-1 w-full rounded border px-2 py-1.5 text-sm" /></label>
            <label className="text-xs">Largo (cm)<input type="number" min={0} value={form.largo} onChange={(e) => setForm({ ...form, largo: Number(e.target.value) })} className="mt-1 w-full rounded border px-2 py-1.5 text-sm" /></label>
            <label className="text-xs">Color<select value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value as ColorKey })} className="mt-1 w-full rounded border px-2 py-1.5 text-sm">{(Object.keys(COLOR_LABELS) as ColorKey[]).map((c) => <option key={c} value={c}>{COLOR_LABELS[c]}</option>)}</select></label>
            <label className="text-xs">Micrones<input type="number" min={0} value={form.microns} onChange={(e) => setForm({ ...form, microns: Number(e.target.value) })} className="mt-1 w-full rounded border px-2 py-1.5 text-sm" /><span className="text-[11px] text-gray-500">0 = cualquiera</span></label>
            <label className="text-xs">Precio por bolsa sin IVA (ARS)<input type="number" min={0} step="0.01" value={form.unitPrice} onChange={(e) => setForm({ ...form, unitPrice: Number(e.target.value) })} className="mt-1 w-full rounded border px-2 py-1.5 text-sm" /><span className="text-[11px] text-gray-500">Pack: {formatARS(form.unitPrice * PACK_SIZE)}</span></label>
            <label className="text-xs">Nota<input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Ej: precio de octubre" className="mt-1 w-full rounded border px-2 py-1.5 text-sm" /></label>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setForm(null)} className="rounded-lg border px-3 py-1.5 text-sm">Cancelar</button>
            <button onClick={save} disabled={saving} className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm text-white disabled:opacity-50">{saving ? 'Guardando...' : 'Guardar precio'}</button>
          </div>
        </div>
      )}
      {loading ? <p className="py-8 text-center text-sm text-gray-500">Cargando...</p> : !prices.length ? (
        <p className="px-5 py-8 text-center text-sm text-gray-500">Este proveedor todavía no tiene precios cargados.{(data?.lastPaid || []).length > 0 && ' Podés empezar con "Completar con los últimos remitos".'}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500"><tr><th className="px-4 py-2">Artículo</th><th className="px-4 py-2 text-right">Por bolsa sin IVA</th><th className="px-4 py-2 text-right">Por pack sin IVA</th><th className="px-4 py-2 text-right">Último pagado</th><th className="px-4 py-2">Nota</th><th className="px-4 py-2" /></tr></thead>
            <tbody className="divide-y">
              {prices.map((price) => {
                const last = suggestSupplierPrice({ prices: [], lastPaid: data?.lastPaid || [] }, price);
                return (
                  <tr key={price.id}>
                    <td className="px-4 py-2">{price.ancho} × {price.largo} cm <span className="ml-1 rounded px-2 py-0.5 text-xs font-semibold" style={receiptColorStyle(price.color)}>{COLOR_LABELS[price.color as ColorKey] || price.color}</span>{price.microns > 0 && <span className="ml-1 text-gray-500">· {price.microns}μ</span>}</td>
                    <td className="px-4 py-2 text-right font-semibold">{formatARS(price.unitPrice)}</td>
                    <td className="px-4 py-2 text-right">{formatARS(price.unitPrice * PACK_SIZE)}</td>
                    <td className={`px-4 py-2 text-right text-xs ${last && last.unitPrice !== price.unitPrice ? 'text-amber-700' : 'text-gray-500'}`}>{last ? <>{formatARS(last.unitPrice)}<span className="block">{formatDate(last.receiptDate || '')}</span></> : '-'}</td>
                    <td className="px-4 py-2 text-xs text-gray-600">{price.note}<span className="block text-gray-400">Actualizado {formatDate(price.updatedAt)}</span></td>
                    <td className="px-4 py-2 text-right">{!isReadOnly && <div className="flex justify-end gap-1">
                      <button onClick={() => setForm({ id: price.id, ancho: price.ancho, largo: price.largo, color: price.color as ColorKey, microns: price.microns, unitPrice: price.unitPrice, note: price.note })} title="Editar" className="rounded border p-1.5 text-gray-600 hover:bg-gray-50"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => remove(price)} title="Quitar" className="rounded border border-red-200 p-1.5 text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {unlisted.length > 0 && prices.length > 0 && (
        <p className="border-t px-4 py-3 text-xs text-gray-500">Comprados a este proveedor pero sin precio en la lista: {unlisted.map((item) => `${item.ancho}×${item.largo} ${COLOR_LABELS[item.color as ColorKey] || item.color}${item.microns ? ` ${item.microns}μ` : ''}`).join(' · ')}. En el remito se propone el último precio pagado.</p>
      )}
    </section>
  );
};

export default SupplierPriceList;
